import { Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Length, PlatformProduct } from '~/graphql/graphql';

interface UniqueProductsCardProps {
  platformProduct: PlatformProduct;
  sellersCount: number;
  listingsCount: number;
  totalStock: number;
  priceMin: number;
  priceMax: number;
  leadTimeMin: number;
  leadTimeMax: number;
  unit: string;
  onClick: () => void;
  lengths: Length[];
}

export function UniqueProductsCard({
  platformProduct,
  sellersCount,
  listingsCount,
  totalStock,
  priceMin,
  priceMax,
  leadTimeMin,
  leadTimeMax,
  unit = 'MBF',
  lengths,
  onClick,
}: UniqueProductsCardProps) {
  const { t } = useTranslation('product');

  return (
    <div
      data-testid="unique-products-card"
      className="bg-white rounded-lg border border-gray-200 hover:border-prim hover:shadow-lg transition-all shadow-sm flex flex-col"
    >
      <div className="cursor-pointer flex-1 flex flex-col">
        <div className="h-48 bg-gradient-to-br from-amber-50 to-amber-100 rounded-t-lg flex items-center justify-center">
          <div className="text-4xl">🌲</div>
        </div>
        <div className="p-4 sm:p-6 flex-1 flex flex-col">
          <div className="flex-1">
            <div className="mb-3">
              <h3 className="text-neut-900 mb-1 font-semibold line-clamp-2">
                {platformProduct.name}
              </h3>
              <p className="text-xs text-neut-500 font-normal">
                {t('product.sellers_listings', { sellersCount, listingsCount })}
              </p>
            </div>
            <div className="space-y-2 mb-4 pb-4 border-b border-gray-100">
              <p className="text-xs text-gray-600 font-normal">
                {t('product.species')}:{' '}
                <span
                  data-testid="product-species"
                  className="text-gray-900 font-medium"
                >
                  {platformProduct.properties?.species?.code}
                </span>
              </p>
              <p className="text-xs text-gray-600 font-normal">
                {t('product.grade')}:{' '}
                <span
                  data-testid="product-grade"
                  className="text-gray-900 font-medium"
                >
                  {platformProduct.properties?.grade?.code}
                </span>
              </p>
              <div className="flex items-start gap-1.5">
                <span className="text-xs text-gray-600 font-normal flex-shrink-0">
                  {t('product.lengths')}:
                </span>
                <div className="flex flex-wrap gap-1">
                  {lengths?.map((length, index) => (
                    <span
                      key={index}
                      className="text-xs px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded font-medium"
                    >
                      {length?.displayValue}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4 pb-4 border-b border-gray-100">
              <div>
                <p className="text-xs text-gray-500 font-normal mb-1">
                  {t('product.total_stock')}
                </p>
                <p
                  data-testid="product-total-stock"
                  className="text-sm text-gray-900 font-semibold"
                >
                  {totalStock} {unit}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-normal mb-1">
                  {t('product.lead_time')}
                </p>
                <p className="text-sm text-gray-900 font-semibold">
                  {' '}
                  {getLeadTime(leadTimeMin, leadTimeMax)} {t('product.days')}
                </p>
              </div>
            </div>
            <div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl text-neut-900 font-bold">
                  ${priceMin}
                </span>
                <span className="text-base text-gray-600 font-normal">-</span>
                <span className="text-2xl text-neut-900 font-bold">
                  ${priceMax}
                </span>
                <span className="text-base text-gray-600 font-normal">
                  /{unit}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="px-4 sm:px-6 pb-4 sm:pb-6">
        <button
          onClick={onClick}
          className="w-full py-2.5 bg-prim text-white rounded-lg hover:bg-prim-hover transition-colors flex items-center justify-center gap-2 text-sm font-medium shadow-sm cursor-pointer"
        >
          <Eye size={16} aria-hidden="true" />
          {t('product.compare_sellers', { count: sellersCount })}
        </button>
      </div>
    </div>
  );
}

function getLeadTime(min: number, max: number): string {
  if (min === max) {
    return min.toString();
  }
  return `${min}-${max}`;
}
